import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { isPresent } from '@ember/utils';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { type IExtraPriceSchema } from 'uplisting-frontend/models/extra-price';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import { invalidAttributeDetail } from 'uplisting-frontend/utils/http';

type Booking = ModelRegistry['booking'];

const DATE_ATTRIBUTES = ['checkIn', 'checkOut'];

export default class BookingDrawerService extends Service {
  @service analytics!: Services['analytics'];
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];
  @service store!: Services['store'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  @tracked booking?: Booking;
  @tracked changeset?: GenericChangeset<Booking>;
  @tracked isSubmitting = false;
  @tracked isUpdatingPrice = false;
  @tracked hasInvalidInput = false;

  #quoteCount = 0;
  #quotedFor?: string;

  get isOpen(): boolean {
    return this.booking !== undefined;
  }

  get hasChangedProperty(): boolean {
    const { booking } = this;

    return !!booking?.newProperty && booking.newProperty !== booking.property;
  }

  get hasChanges(): boolean {
    const { booking, changeset } = this;

    if (!booking || !changeset || this.#isGone(booking)) {
      return false;
    }

    return (
      changeset.isDirty ||
      booking.hasDirtyAttributes ||
      !!booking.price?.hasDirtyAttributes ||
      this.hasChangedProperty ||
      this.#isMoving(booking)
    );
  }

  get canUpdate(): boolean {
    return (
      this.hasChanges &&
      !this.isSubmitting &&
      !this.isUpdatingPrice &&
      !this.hasInvalidInput &&
      !this.changeset?.isInvalid
    );
  }

  public open(booking: Booking): void {
    if (this.booking === booking) {
      return;
    }

    this.booking = booking;
    this.changeset = this.bookingRepository.buildChangeset(booking);
    this.hasInvalidInput = false;
    this.#quotedFor = undefined;
  }

  public close(): void {
    this.discard();

    this.booking = undefined;
    this.changeset = undefined;
    this.hasInvalidInput = false;
  }

  public changesetFor(booking: Booking): GenericChangeset<Booking> {
    if (this.booking === booking && this.changeset) {
      return this.changeset;
    }

    return this.bookingRepository.buildChangeset(booking);
  }

  public discard(): void {
    const { booking, changeset } = this;

    if (!booking) {
      return;
    }

    changeset?.rollback();

    this.#quotedFor = undefined;

    if (!this.#isGone(booking)) {
      booking.rollbackAttributes();
      booking.newProperty = undefined;
      booking.newMultiUnit = undefined;

      const { price } = booking;

      if (price) {
        price.rollbackAttributes();
        price.dropOrphanedExtraPrices();
      }
    }

    const { interaction, data } = this.calendarManager;

    if (interaction.resizeOperation?.record.model === booking) {
      interaction.resetResizeOperation();
      data.setupFasterLookup();
    } else if (interaction.dragOperation?.record.model === booking) {
      interaction.resetDragOperation();
      data.setupFasterLookup();
    }
  }

  public async requoteStaged(): Promise<void> {
    const { booking, changeset } = this;

    if (!booking?.price || !changeset) {
      return;
    }

    try {
      await this.requote(booking, changeset);
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  public async requoteIfStale(
    booking: Booking,
    changeset: GenericChangeset<Booking>,
  ): Promise<void> {
    if (
      this.booking === booking &&
      this.#quotedFor === this.#quoteKey(changeset)
    ) {
      return;
    }

    await this.requote(booking, changeset);
  }

  @waitFor
  public async requote(
    booking: Booking,
    changeset: GenericChangeset<Booking>,
  ): Promise<void> {
    const { price } = changeset;
    const quoteNumber = ++this.#quoteCount;
    const isLatest = () => quoteNumber === this.#quoteCount;

    this.isUpdatingPrice = true;

    try {
      booking.price?.dropOrphanedExtraPrices();

      const quote = this.store.createRecord('quote', {
        booking,
        charges: price.charges.map((item) => item.toObject()),
        discounts: price.discounts.map((item) => item.toObject()),
        property: booking.property,
        checkIn: changeset.checkIn,
        checkOut: changeset.checkOut,
        cleaningFee: price.cleaningFee,
        numberOfGuests: price.numberOfGuests,
      });

      try {
        await quote.save();
      } catch (error) {
        this.store.unloadRecord(quote);

        throw error;
      }

      if (!isLatest()) {
        this.store.unloadRecord(quote);

        return;
      }

      Object.assign(changeset.price, quote.toObject());

      this.#unloadRecords(price.charges);
      this.#unloadRecords(price.discounts);

      changeset.price.charges = this.#toExtraPrices(quote.charges);
      changeset.price.discounts = this.#toExtraPrices(quote.discounts);

      changeset.execute();

      this.store.unloadRecord(quote);

      if (this.booking === booking) {
        this.#quotedFor = this.#quoteKey(changeset);
      }
    } finally {
      if (isLatest()) {
        this.isUpdatingPrice = false;
      }
    }
  }

  @waitFor
  public async update(): Promise<void> {
    const { booking, changeset } = this;

    if (!booking || !changeset || !this.canUpdate) {
      return;
    }

    this.isSubmitting = true;

    try {
      await changeset.validate();

      if (!changeset.isInvalid) {
        await this.#commit(booking, changeset);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  async #commit(
    booking: Booking,
    changeset: GenericChangeset<Booking>,
  ): Promise<void> {
    const { price } = booking;
    const saved = this.#savedDates(booking);
    const unit = this.#movingToUnit(booking);
    const newProperty = this.hasChangedProperty
      ? booking.newProperty
      : undefined;

    const guestNameChanged = this.#hasChange(changeset, 'rawGuestName');
    const numberOfGuestsChanged = this.#hasChange(
      changeset,
      'price.numberOfGuests',
    );
    const priceChanged =
      !!price?.hasDirtyAttributes ||
      changeset.changes.some((change) =>
        String(change['key']).startsWith('price.'),
      );

    changeset.execute();

    if (guestNameChanged) {
      booking.guestName = booking.rawGuestName;
    }

    const { checkIn, checkOut } = booking;
    const datesChanged =
      checkIn !== saved.checkIn || checkOut !== saved.checkOut;
    const savedLockCode = booking.changedAttributes()['customLockCode']?.[0] as
      | string
      | null
      | undefined;
    const staged = this.#stagedAttributes(booking);

    try {
      if (datesChanged && !price) {
        throw new Error(`Booking ${booking.id} has no price to move its dates`);
      }

      if (unit || newProperty) {
        booking.newMultiUnit = unit;

        await this.#keepingPrice(booking, () => booking.save());

        if (unit) {
          booking.multiUnit = unit;

          this.calendarManager.interaction.keepDraggedUnit();
        }

        if (newProperty) {
          this.analytics.trackEvent(AnalyticsEvents.bookingMoved);
        }
      }

      if (price && (datesChanged || priceChanged)) {
        if (numberOfGuestsChanged) {
          price.hostEdited = true;
        }

        price.checkIn = checkIn;
        price.checkOut = checkOut;

        await price.save();

        price.dropOrphanedExtraPrices();

        await booking.reload();
      }

      await this.#saveAttributes(booking, staged, savedLockCode);

      booking.newProperty = undefined;
      booking.newMultiUnit = undefined;

      this.changeset = this.bookingRepository.buildChangeset(booking);
      this.#quotedFor = undefined;
      this.calendarManager.interaction.clearOperations();

      this.notifications.info('notifications.booking.updated');
    } catch (error) {
      booking.newMultiUnit = undefined;

      Object.assign(booking, staged, { checkIn, checkOut });

      const detail = invalidAttributeDetail(error);

      if (detail) {
        this.notifications.error(detail, true);
      } else {
        this.sentry.captureException(error);
        this.notifications.error();
      }
    }
  }

  @waitFor
  public async saveNow(
    booking: Booking,
    attributes: Partial<Booking>,
  ): Promise<void> {
    if (this.booking !== booking) {
      Object.assign(booking, attributes);

      await booking.save();

      return;
    }

    const held = Object.entries(booking.changedAttributes()).filter(
      ([key]) => !(key in attributes),
    );
    const { newProperty, multiUnit } = booking;

    Object.assign(
      booking,
      Object.fromEntries(held.map(([key, change]) => [key, change?.[0]])),
      attributes,
      { newProperty: undefined },
    );

    try {
      await this.#keepingPrice(booking, () => booking.save());
    } finally {
      Object.assign(
        booking,
        Object.fromEntries(held.map(([key, change]) => [key, change?.[1]])),
        { newProperty, multiUnit },
      );
    }
  }

  // a booking save returns its embedded price, which must not replace a staged one
  async #keepingPrice(
    booking: Booking,
    save: () => Promise<unknown>,
  ): Promise<void> {
    const { price } = booking;

    if (!price?.hasDirtyAttributes) {
      await save();

      return;
    }

    const changed = Object.fromEntries(
      Object.entries(price.changedAttributes()).map(([key, change]) => [
        key,
        change?.[1],
      ]),
    );
    const charges = [...price.charges];
    const discounts = [...price.discounts];

    try {
      await save();
    } finally {
      Object.assign(price, changed, { charges, discounts });
    }
  }

  #quoteKey(changeset: GenericChangeset<Booking>): string {
    return [
      changeset.checkIn,
      changeset.checkOut,
      changeset.price.numberOfGuests,
    ].join('|');
  }

  #toExtraPrices(items: IExtraPriceSchema[]): ModelRegistry['extra-price'][] {
    return items
      .filter((item) => isPresent(item.amount))
      .map((item) => this.store.createRecord('extra-price', item));
  }

  #unloadRecords(records: ModelRegistry['extra-price'][]): void {
    records.forEach((record) => {
      this.store.unloadRecord(record);
    });
  }

  #isGone(booking: Booking): boolean {
    return booking.isDeleted || booking.isDestroying || booking.isDestroyed;
  }

  #isMoving(booking: Booking): boolean {
    const { dragOperation, resizeOperation } = this.calendarManager.interaction;

    return (
      (dragOperation?.finished === true &&
        dragOperation.record.model === booking) ||
      (resizeOperation?.finished === true &&
        resizeOperation.record.model === booking)
    );
  }

  #movingToUnit(booking: Booking): ModelRegistry['multi-unit'] | undefined {
    const { dragOperation, dragChangedMultiUnit } =
      this.calendarManager.interaction;

    if (!dragChangedMultiUnit || dragOperation?.record.model !== booking) {
      return undefined;
    }

    return dragOperation.location as ModelRegistry['multi-unit'];
  }

  #hasChange(changeset: GenericChangeset<Booking>, key: string): boolean {
    return changeset.changes.some((change) => change['key'] === key);
  }

  #savedDates(booking: Booking): { checkIn: string; checkOut: string } {
    const { checkIn, checkOut } = booking.changedAttributes();

    return {
      checkIn: checkIn ? (checkIn[0] as string) : booking.checkIn,
      checkOut: checkOut ? (checkOut[0] as string) : booking.checkOut,
    };
  }

  // dates are only ever saved with the price, which re-prices the stay
  #keepSavedDates(booking: Booking): void {
    const saved = this.#savedDates(booking);

    booking.checkIn = saved.checkIn;
    booking.checkOut = saved.checkOut;
  }

  #stagedAttributes(booking: Booking): Partial<Booking> {
    const changed = booking.changedAttributes();

    return Object.fromEntries(
      Object.entries(changed)
        .filter(([key]) => !DATE_ATTRIBUTES.includes(key))
        .map(([key, change]) => [key, change?.[1]]),
    ) as Partial<Booking>;
  }

  // a lock code is saved on its own, as the API then ignores every other attribute
  async #saveAttributes(
    booking: Booking,
    staged: Partial<Booking>,
    savedLockCode: string | null | undefined,
  ): Promise<void> {
    const { customLockCode, ...attributes } = staged;

    Object.assign(booking, attributes);

    this.#keepSavedDates(booking);

    if (Object.keys(attributes).length > 0) {
      if (customLockCode !== undefined) {
        booking.customLockCode = savedLockCode ?? null;
      }

      await booking.save();
    }

    if (customLockCode !== undefined) {
      booking.customLockCode = customLockCode;

      await booking.save();
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'booking-drawer': BookingDrawerService;
  }
}
